import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import BuyerInvitationIcon from "../../../../assets/img/action-center/buyer-invitation.svg";
import ChevronRightWhiteIcon from "../../../../assets/img/action-center/chevron-right-white.svg";
import { getClientLogo } from "../../../../utils/clientLogos";
import useHeapCardViewed from "../../../../hooks/useHeapCardViewed";
import CorridorRequestModal from "../corridor-request-modal/CorridorRequestModal";
import "./CorridorCard.scss";
import "./CorridorRequestCard.scss";

const FALLBACK_COLOR = "#3B82F6";
const VP_TRUNCATE_THRESHOLD = 140;

const CorridorRequestCard = ({ data, onDismiss, cardPosition, hotelName }) => {
  const cardRef = useHeapCardViewed(
    "corridor-request",
    cardPosition,
    hotelName
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submissionState, setSubmissionState] = useState("idle"); // 'idle' | 'submitted'
  const [isVpExpanded, setIsVpExpanded] = useState(false);

  if (!data) return null;

  const { recommendationId, ownerName, valueProposition } = data;
  const buyerLabel = ownerName || "Buyer";
  const isSubmitted = submissionState === "submitted";
  const logo = getClientLogo(ownerName);

  const vpText = valueProposition || "";
  const shouldTruncate =
    !!vpText && vpText.length > VP_TRUNCATE_THRESHOLD && !isVpExpanded;
  const displayedVp = shouldTruncate
    ? `${vpText.slice(0, VP_TRUNCATE_THRESHOLD).trimEnd()}…`
    : vpText;

  const handleMaybeLater = () => {
    onDismiss?.();
  };

  const handleOpenNow = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalSuccess = (action) => {
    setIsModalOpen(false);
    if (action === "APPROVE") {
      setSubmissionState("submitted");
    } else {
      onDismiss?.();
    }
  };

  return (
    <div
      ref={cardRef}
      className={`corridor-card corridor-request-card${
        isSubmitted ? " corridor-card--submitted" : ""
      }`}
    >
      <div className="corridor-card-badge buyer-invitation-badge">
        <img src={BuyerInvitationIcon} alt="" className="badge-icon" />
        <span>Buyer Invitation</span>
      </div>

      <h3 className="corridor-card-title crq-title">
        {buyerLabel} would like to connect
      </h3>

      {isSubmitted ? (
        <p className="corridor-card-description">
          Your response has been sent. We&apos;ll notify the buyer.
        </p>
      ) : (
        <p className="corridor-card-description crq-subtitle">
          Unlock demand instantly by opening a corridor with {buyerLabel}.
        </p>
      )}

      <ul className="corridor-card-clients crq-clients">
        <li className="corridor-card-client">
          <div className="client-icon-wrapper">
            {logo ? (
              <img src={logo} alt={buyerLabel} className="client-icon" />
            ) : (
              <span
                data-testid={`corridor-request-fallback-${recommendationId}`}
                className="client-icon client-icon-fallback"
                style={{ backgroundColor: FALLBACK_COLOR }}
              />
            )}
          </div>
          <span className="client-name">{buyerLabel}</span>
        </li>
      </ul>

      {vpText && (
        <div
          className={`crq-vp-block${
            isVpExpanded ? " crq-vp-block--expanded" : ""
          }`}
        >
          <div className="crq-vp-label">Value proposition</div>
          <p className="crq-vp-text">
            {displayedVp}
            {shouldTruncate && (
              <>
                {" "}
                <button
                  type="button"
                  className="crq-vp-read-more"
                  onClick={() => setIsVpExpanded(true)}
                >
                  read more
                </button>
              </>
            )}
          </p>
        </div>
      )}

      <div className="corridor-card-actions">
        {isSubmitted ? (
          <div className="request-sent-pill">
            <span>Request Sent</span>
          </div>
        ) : (
          <>
            <div className="corridor-card-actions-left">
              <Button
                id={`action-corridor-request-later-${recommendationId}`}
                appearance="link"
                className="maybe-later-btn"
                onClick={handleMaybeLater}
              >
                Maybe Later
              </Button>
            </div>
            <Button
              id={`action-corridor-request-open-${recommendationId}`}
              appearance="primary"
              className="open-now-btn"
              onClick={handleOpenNow}
            >
              Open Now
              <img src={ChevronRightWhiteIcon} alt="" className="btn-chevron" />
            </Button>
          </>
        )}
      </div>

      {isModalOpen && (
        <CorridorRequestModal
          data={data}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

CorridorRequestCard.propTypes = {
  data: PropTypes.shape({
    recommendationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    sourceCorridorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ownerActor: PropTypes.string,
    ownerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ownerName: PropTypes.string,
    clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    propertyName: PropTypes.string,
    justificationCodes: PropTypes.arrayOf(PropTypes.string),
    valueProposition: PropTypes.string,
    expiryDate: PropTypes.string,
  }).isRequired,
  onDismiss: PropTypes.func,
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
};

export default CorridorRequestCard;
